<script setup lang="ts">
import { ApiError } from "../lib/api"
import { makeOffer, type Thread } from "../composables/useMessaging"
import { t } from "../lib/i18n"

// A sales channel offer to the other shop: the commission, and a few words. Johku checks that the
// offering shop has a sales channel, and puts the agreement into effect once the other shop accepts.
const props = defineProps<{ thread: Thread; asOrganisationId: string }>()
const emit = defineEmits<{ close: [] }>()
const other = computed(() => props.thread.organisations.find((o) => o.id !== props.asOrganisationId)?.name ?? "")
const commission = ref(10)
const note = ref("")
const busy = ref(false)
const error = ref("")

async function send() {
  busy.value = true
  error.value = ""
  try {
    await makeOffer(props.thread.id, { asOrganisationId: props.asOrganisationId, commission: Number(commission.value), note: note.value })
    emit("close")
  } catch (err) {
    error.value =
      err instanceof ApiError && err.code === "no_sales_channel"
        ? t("Your shop has no sales channel in Johku. Set one up in the Johku app's settings first.")
        : err instanceof Error
          ? err.message
          : String(err)
  } finally {
    busy.value = false
  }
}
</script>

<template>
  <HubSheet :title="t('Offer a sales channel')" @close="emit('close')">
    <form class="form" @submit.prevent="send">
      <p class="lede">{{ t("{shop} can sell its products in your sales channel. Once they accept, Johku puts it into effect.", { shop: other }) }}</p>
      <label>
        {{ t("Commission (%)") }}
        <input v-model="commission" class="hub-field" type="number" min="0" max="100" step="0.5" required />
      </label>
      <label>
        {{ t("Message (optional)") }}
        <textarea v-model="note" class="hub-field" rows="3" maxlength="2000" />
      </label>
      <p v-if="error" class="error" role="alert">{{ error }}</p>
      <div class="actions">
        <button class="hub-btn hub-btn-quiet" type="button" @click="emit('close')">{{ t("Cancel") }}</button>
        <button class="hub-btn" type="submit" :disabled="busy">{{ t("Send the offer") }}</button>
      </div>
    </form>
  </HubSheet>
</template>

<style scoped>
.form {
  display: grid;
  gap: 14px;
  padding: 0 8px 4px;
}

.lede {
  margin: 0;
  color: var(--hub-muted);
}

label {
  display: grid;
  gap: 6px;
  font-weight: 600;
  font-size: 0.9rem;
}

textarea.hub-field {
  resize: vertical;
  font: inherit;
}

.error {
  color: var(--hub-danger);
  margin: 0;
}

.actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}
</style>
